import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined, StarOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import TextAlert from 'common/components/alert.component';
import Confirm from 'common/components/confirm.component';
import AppDrawer from 'common/components/drawer.component';
import AppList from 'common/components/list.component';
import Loading from 'common/components/loading.component';
import TitlePage from 'common/components/title-page.component';
import AddressForm from '../address-form.component';
import AddressListItem from './address-list-item.component';

import { DeviceTypeList, TextAlertTypes } from 'config/constants/global.constants';
import { AddressTranscription, GeneralTranscription } from 'config/constants/transcription.constant';
import { DrawerActionType } from 'config/constants/user.constants';
import UserService from 'config/services/user.service';
import { MAX_ADDRESS_QUANTITY } from 'config/utils/address.util';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import { isAuthError } from 'config/utils/errors.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';

import { RootState } from 'store/index';
import { Address } from 'store/models/user.model';
import { UserSlice } from 'store/slices/user.slice';

import { useDeviceType } from 'config/hooks/useDeviceType.hook';
import { DeviceType } from 'config/types/common.type';

type AddressListProps = {
  addressData: Address[];
  withTitle?: boolean;
  callBack?: (value: any) => void;
};

const AddressList: React.FC<AddressListProps> = ({ addressData, withTitle = true, callBack }) => {
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
  const [drawerTitle, setDrawerTitle] = useState<string>(AddressTranscription.NEW_ADDRESS);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const deviceType: DeviceType = useDeviceType();

  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state);

  const handleGetUserProfile = useCallback(async () => {
    const getUserProfileResult = await UserService.getUserProfile();
    dispatch(UserSlice.refreshUserProfile(getUserProfileResult));
  }, [dispatch]);

  const handleOpenAddressDrawer = useCallback((isOpen: boolean, actionType?: DrawerActionType): void => {
    if (actionType === DrawerActionType.ADD) {
      setDrawerTitle(AddressTranscription.NEW_ADDRESS);
    }
    setIsOpenDrawer(isOpen);
  }, []);

  const handleFormCallBack = useCallback(
    (value: boolean): void => {
      handleOpenAddressDrawer(value);
    },
    [handleOpenAddressDrawer],
  );

  const handleSelectAddress = useCallback(
    async (addressId: string) => {
      await AuthIntentUtils.withAuthRetry(async () => {
        setIsLoading(true);
        try {
          const result = await UserService.selectUserAddress(addressId);
          if (result) {
            await handleGetUserProfile();
            SnackbarUtilities.success(AddressTranscription.SELECTED_SUCCES_ADDRESS);
            callBack?.(false);
          }
        } catch (error) {
          if (isAuthError(error)) throw error;
        } finally {
          setIsLoading(false);
        }
      });
    },
    [handleGetUserProfile, callBack],
  );

  const handleDeleteAddress = useCallback(
    async (addressId: string) => {
      await AuthIntentUtils.withAuthRetry(async () => {
        setIsLoading(true);
        try {
          const result = await UserService.deleteUserAddress(addressId);
          if (result) {
            await handleGetUserProfile();
            SnackbarUtilities.success(AddressTranscription.DELETED_SUCCES_ADDRESS);
            callBack?.(false);
          }
        } catch (error) {
          if (isAuthError(error)) throw error;
        } finally {
          setIsLoading(false);
        }
      });
    },
    [handleGetUserProfile, callBack],
  );

  const handleActionsItems = useCallback(
    (address: Address): React.ReactNode[] => {
      return [
        !address.selected && (
          <Confirm
            placement='topRight'
            title={AddressTranscription.SELECT_ADDRESS}
            description={AddressTranscription.SELECT_ADDRESS_CONFIRMATION}
            onConfirm={() => handleSelectAddress(address._id)}
            key={AddressTranscription.SELECT_ADDRESS}
          >
            <Button type='text'>
              <StarOutlined />
              {GeneralTranscription.SELECT}
            </Button>
          </Confirm>
        ),
        <Confirm
          placement='topLeft'
          title={AddressTranscription.DELETE_ADDRESS}
          description={AddressTranscription.DELETE_ADDRESS_CONFIRMATION}
          icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
          onConfirm={() => handleDeleteAddress(address._id)}
          key={AddressTranscription.DELETE_ADDRESS}
        >
          <Button type='text' danger>
            <DeleteOutlined />
            {GeneralTranscription.DELETE}
          </Button>
        </Confirm>,
      ];
    },
    [handleDeleteAddress, handleSelectAddress],
  );

  const renderItem = useCallback(
    (item: Address, index: number): React.ReactNode => (
      <AddressListItem key={index} item={item} actions={handleActionsItems(item)} selected={item.selected} />
    ),
    [handleActionsItems],
  );

  return (
    <React.Fragment>
      {addressData.length >= MAX_ADDRESS_QUANTITY && (
        <TextAlert text={AddressTranscription.MAX_ADDRESS_CREATED} type={TextAlertTypes.WARNING} showIcon />
      )}
      {withTitle && (
        <TitlePage
          text={AddressTranscription.MY_ADDRESSES}
          optionalStyles={deviceType !== DeviceTypeList.DESKTOP ? { display: 'block' } : undefined}
          actions={
            <Button
              onClick={() => handleOpenAddressDrawer(true, DrawerActionType.ADD)}
              disabled={isLoading || addressData.length >= MAX_ADDRESS_QUANTITY}
            >
              <PlusOutlined />
              {AddressTranscription.NEW_ADDRESS}
            </Button>
          }
        />
      )}
      <Loading rotate={isLoading} text={AddressTranscription.UPDATING_ADDRESS}>
        <AppList dataSource={addressData} renderItem={renderItem} />
      </Loading>
      <AppDrawer title={drawerTitle} isOpen={isOpenDrawer} onClose={() => handleOpenAddressDrawer(false)}>
        <Loading rotate={user.isFetching} text={AddressTranscription.SAVING_ADDRESS}>
          <AddressForm callBack={handleFormCallBack} />
        </Loading>
      </AppDrawer>
    </React.Fragment>
  );
};

export default AddressList;
